import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import localEs from '@angular/common/locales/es';
import { AngularFireModule } from '@angular/fire/compat';
import {
  AngularFireAnalytics,
  AngularFireAnalyticsModule,
  DEBUG_MODE,
  ScreenTrackingService,
} from '@angular/fire/compat/analytics';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { QuillModule } from 'ngx-quill';
import { NotFoundModule } from 'src/app/pages/not-found/not-found.module';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ComponentsModule } from './components/components.module';
import Counter from './counter';
import { InterceptorService } from './services/interceptor.service';
import { LogService } from './services/log.service';
import { LoggerManagerService } from './services/logger-manager.service';
import { firstValueFrom } from 'rxjs';

registerLocaleData(localEs, 'es');

function initializeKeycloak(keycloak: KeycloakService): () => Promise<boolean> {
  return () =>
    keycloak.init({
      config: {
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId,
      },
      initOptions: {
        checkLoginIframe: false,
      },
      // ENDPOITNS SIN INYECCION DE TOKEN EN ENCABEZADOS
      bearerExcludedUrls: [],
    });
}

function initializeLoggerService(
  googleAnalytics: AngularFireAnalytics
): LoggerManagerService {
  return new LoggerManagerService(googleAnalytics, environment.loggerStrategy);
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function appInitializerFactory(translate: TranslateService) {
  return () => {
    const browserLang = translate.getBrowserLang() || 'es';
    translate.setDefaultLang('es');
    return firstValueFrom(translate.use(browserLang));
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    KeycloakAngularModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAnalyticsModule,
    MatButtonModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    ComponentsModule,
    NotFoundModule,
  ],
  providers: [
    LogService,
    { provide: MAT_DATE_LOCALE, useValue: 'es-MX' },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    {
      provide: LoggerManagerService,
      useFactory: initializeLoggerService,
      deps: [AngularFireAnalytics],
    },
    // MODO DEBUG SOLO PARA DESARROLLO, ELIMINAR EN PROYECTO REAL
    { provide: DEBUG_MODE, useValue: true },
    // ESTE SERVICIO HACE LOG AUTOMÁTICO DE NAVEGACIÓN ENTRE PANTALLAS BASADO EN ANGULAR ROUTER
    // https://github.com/angular/angularfire/blob/master/docs/analytics/getting-started.md#tracking-screen-views
    ScreenTrackingService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptorService,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'es',
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
